import React from "react";
import Textbox from "carbon-react/lib/__experimental__/components/textbox";
import TextArea from "carbon-react/lib/__experimental__/components/textarea";
import StepHeading from "../../common/stepHeading/StepHeading";
import StepTitle from "../../common/stepTitle/StepTitle";
import LanguageSelector from "../../common/languageSelector/LanguageSelector";
import { isAValidEmailAddress } from "../../../constants/validation";
import DialogConfirmButtons from "../../common/dialogConfirmButtons/DialogConfirmButtons";

const EnterEmailStep = ({
  user,
  busy,
  state,
  onStateChanged,
  onCancel,
  onCompleted,
}) => {
  const onCancelClicked = () => {
    if (isAValidFunc(onCancel)) onCancel();
  };

  const onSendClicked = () => {
    if (!validateStep()) return;
    if (isAValidFunc(onCompleted)) onCompleted();
  };

  const validateStep = () => {
    let result = true;

    if (!validateEmail()) result = false;
    if (!validateConfirmEmail()) result = false;
    if (!validateLanguageCode()) result = false;
    if (!validateAgentComments()) result = false;

    return result;
  };

  const isAValidFunc = (func) => {
    return func && typeof func === "function";
  };

  const setEmailValid = (valid, validationMsg = "") => {
    updateState(() => {
      state.emailValid = valid;
      state.emailValidationMsg = validationMsg;
    });
  };

  const setConfirmEmailValid = (valid, validationMsg = "") => {
    updateState(() => {
      state.confirmEmailValid = valid;
      state.confirmEmailValidationMsg = validationMsg;
    });
  };

  const validateEmail = () => {
    if (!isAValidEmailAddress(state.email)) {
      setEmailValid(false, "Please enter a valid email");
      return false;
    }

    if (state.email === user?.email.trim()) {
      setEmailValid(false, "This is the user's current email");
      return false;
    }

    if (!state.confirmEmailValid) validateConfirmEmail();

    setEmailValid(true);
    return true;
  };

  const validateConfirmEmail = () => {
    if (!isAValidEmailAddress(state.confirmEmail)) {
      setConfirmEmailValid(false, "Please enter a valid email");
      return false;
    }

    if (!state.email || state.email !== state.confirmEmail) {
      setConfirmEmailValid(false, "Values do not match");
      return false;
    }

    setConfirmEmailValid(true);
    return true;
  };

  const validateLanguageCode = () => {
    const valid = state.languageCode && state.languageCode.length > 0;
    updateState(() => {
      state.languageCodeValid = valid;
    });
    return valid;
  };

  const validateAgentComments = () => {
    const sanitizedComments = state.agentComments
      ? state.agentComments.trim()
      : "";
    const valid = sanitizedComments.length > 0 ? true : false;
    updateState(() => {
      state.agentComments = sanitizedComments;
      state.agentCommentsValid = valid;
    });
    return valid;
  };

  const onEmailChanged = (email) => {
    const sanitizedEmail = email && email.length > 0 ? email.trim() : "";
    state.email = sanitizedEmail;
    setEmailValid(true);
  };

  const onConfirmEmailChanged = (email) => {
    const sanitizedEmail = email && email.length > 0 ? email.trim() : "";
    state.confirmEmail = sanitizedEmail;
    setConfirmEmailValid(true);
  };

  const onLanguageCodeChanged = (languageCode) => {
    updateState(() => {
      state.languageCodeValid = true;
      state.languageCode = languageCode;
    });
  };

  const onAgentCommentsChanged = (agentComments) => {
    const sanitizedComments =
      agentComments && agentComments.length > 0 ? agentComments : "";
    updateState(() => {
      state.agentCommentsValid = true;
      state.agentComments = sanitizedComments;
    });
  };

  const emailWarningAttributes = {
    hasWarning: true,
    inputIcon: "warning",
    tooltipMessage: state.emailValidationMsg,
  };

  const confirmEmailWarningAttributes = {
    hasWarning: true,
    inputIcon: "warning",
    tooltipMessage: state.confirmEmailValidationMsg,
  };

  const agentCommentsWarningAttributes = {
    hasWarning: true,
    inputIcon: "warning",
    tooltipMessage: "Please indicate why this action is being performed",
  };

  const updateState = (mutateState) => {
    if (isAValidFunc(mutateState)) mutateState();
    onStateChanged({ ...state });
  };

  const emailInput = (
    <Textbox
      id="change-email-new-email"
      autoFocus
      label="New email address"
      disabled={busy}
      value={state?.email}
      onChange={(e) => {
        onEmailChanged(e.target.value);
      }}
      onBlur={validateEmail}
      {...(!state.emailValid ? emailWarningAttributes : {})}
    />
  );

  const confirmEmailInput = (
    <Textbox
      id="change-email-confirm-email"
      label="Confirm new email address"
      disabled={busy}
      value={state?.confirmEmail}
      onChange={(e) => {
        onConfirmEmailChanged(e.target.value);
      }}
      onBlur={validateConfirmEmail}
      {...(!state.confirmEmailValid ? confirmEmailWarningAttributes : {})}
    />
  );

  const languageSelection = (
    <LanguageSelector
      id="change-email-select-language"
      required={false}
      appearBusy={busy}
      value={state.languageCode}
      onValueChanged={(languageCode) => {
        onLanguageCodeChanged(languageCode);
      }}
      onBlur={validateLanguageCode}
      appearInvalid={!state.languageCodeValid}
      validationMsg="Language must be provided"
    />
  );

  const agentCommentsInput = (
    <TextArea
      id="change-email-agent-comments"
      placeholder={agentCommentsWarningAttributes.tooltipMessage}
      label="Comments"
      disabled={busy}
      value={state.agentComments}
      characterLimit={"250"}
      enforceCharacterLimit={true}
      name="changeEmailComments"
      onChange={(e) => {
        onAgentCommentsChanged(e.target.value);
      }}
      onBlur={validateAgentComments}
      {...(!state.agentCommentsValid ? agentCommentsWarningAttributes : {})}
    />
  );

  const buttons = (
    <DialogConfirmButtons
      appearBusy={busy}
      confirmBtnEnabled={true}
      confirmBtnLabel="Send code"
      onConfirm={onSendClicked}
      onCancel={onCancelClicked}
    />
  );

  return (
    <div id="enter-email-step" className="change-email-enter-email-step">
      <div className="change-email-step-body">
        <StepHeading label="Current email address" value={user?.email} />
        <StepTitle title="Enter new email address and send verification code" />
        {emailInput}
        {confirmEmailInput}
        {languageSelection}
        {agentCommentsInput}
      </div>
      {buttons}
    </div>
  );
};

export default EnterEmailStep;
